import { useEffect, useState } from 'react';

/**
 * 지갑주소 표시 패널 — 복사 버튼 + QR.
 * 긴 주소를 드래그로 복사하는 불편을 없애는 게 목적이라, 동작은 두 가지뿐이다:
 *  - 복사: 클립보드에 주소를 넣고 "붙여넣기 하세요" 안내.
 *  - QR: 주소 원문을 QR로 그린다. 상대가 카메라·지갑 앱으로 찍으면 주소가 그대로 잡힌다.
 */
export function WalletPanel({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState('');

  // 복사 안내는 잠깐만 보여주고 치운다.
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2500);
    return () => clearTimeout(t);
  }, [copied]);

  useEffect(() => {
    if (!qrOpen || qrUrl) return;
    // QR 라이브러리는 열 때만 로드한다 — 메인 번들을 키울 이유가 없다.
    void import('qrcode')
      .then((QRCode) => QRCode.toDataURL(address, { margin: 1, width: 220 }))
      .then(setQrUrl)
      .catch(() => setQrUrl(''));
  }, [qrOpen, qrUrl, address]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
    } catch {
      // 클립보드 API가 막힌 환경(구형 웹뷰 등) — 선택이라도 되게 해준다.
      setCopied(false);
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2 rounded-xl bg-ink-50 px-3 py-2.5">
        <code className="min-w-0 flex-1 break-all font-mono text-[12px] leading-relaxed text-ink-700">
          {address}
        </code>
        <button
          onClick={() => void copy()}
          className="shrink-0 rounded-lg bg-tomato-600 px-3 py-1.5 text-[12px] font-medium text-white transition active:scale-[0.97]"
        >
          복사
        </button>
        <button
          onClick={() => setQrOpen((v) => !v)}
          className={`shrink-0 rounded-lg px-3 py-1.5 text-[12px] font-medium transition active:scale-[0.97] ${
            qrOpen ? 'bg-ink-900 text-white' : 'bg-surface text-ink-700 shadow-card'
          }`}
        >
          QR
        </button>
      </div>
      {copied && (
        <p className="mt-1.5 text-[12px] font-medium text-tomato-600">
          지갑주소를 복사했어요. 붙여넣기 하세요.
        </p>
      )}
      {qrOpen && (
        <div className="mt-3 flex flex-col items-center gap-2">
          {qrUrl ? (
            <img src={qrUrl} alt="지갑주소 QR 코드" className="size-[180px] rounded-xl bg-white p-2 shadow-card" />
          ) : (
            <p className="text-[12px] text-ink-400">QR을 만들지 못했어요.</p>
          )}
          <p className="text-center text-[12px] text-ink-400">
            상대방이 이 QR을 찍으면 긴 주소를 드래그하지 않아도
            <br />
            주소가 그대로 복사돼요.
          </p>
        </div>
      )}
    </div>
  );
}
